import { getToken, authenticatedFetch } from '../../../utils/auth';

/** 빌드 플래그: true면 미로그인 시 목업 허용. 로그인 후에는 항상 실 API. */
const ENV_ALLOW_MOCK = process.env.REACT_APP_USE_MOCK !== 'false';

let mockOrderSeq = 2;
const mockPendingOrders = [
  {
    id: 'mock-pending-1',
    symbol: '005930',
    stock_name: '삼성전자',
    order_type: 'buy',
    quantity: 1,
    price: 74500,
    status: 'pending',
    created_at: new Date().toISOString(),
  },
];

const MOCK = {
  balance: {
    total_assets: 12500000,
    cash: 3200000,
    stock_value: 9300000,
    profit_loss: 450000,
    profit_loss_rate: 3.85,
    is_paper_trading: true,
  },
  portfolio: [
    { symbol: '005930', name: '삼성전자', quantity: 10, averagePrice: 72000, currentPrice: 74500, profit_loss: 25000, profit_loss_rate: 3.47 },
    { symbol: '000660', name: 'SK하이닉스', quantity: 5, averagePrice: 198000, currentPrice: 205000, profit_loss: 35000, profit_loss_rate: 3.54 },
  ],
  trades: [{ id: '1', symbol: '005930', stock_name: '삼성전자', order_type: 'buy', quantity: 2, price: 74200, status: 'completed' }],
  recommendations: [{ symbol: '005930', name: '삼성전자', action: 'buy', confidence: 78, reason: '반도체 수요 회복' }],
  config: {
    auto_buy: { enabled: true, confidence_threshold: 70 },
    auto_sell: { enabled: true },
    execution_schedule: {
      mode: 'window',
      window_start: '09:10',
      window_end: '14:50',
      weekdays: [1, 2, 3, 4, 5],
      enforce_market_hours: true,
      timezone: 'Asia/Seoul',
    },
    analysis_config: { update_frequency_weeks: 1 },
  },
  connection: {
    google_ai: { connected: true, message: '연결됨', model: 'gemini-mock' },
    local_ai: { connected: false, message: '미연결' },
    timestamp: new Date().toISOString(),
  },
  aiStatus: {
    active: true,
    last_analysis_time: new Date().toISOString(),
    auto_buy: { enabled: true },
    auto_sell: { enabled: true },
  },
  scheduledOrders: [
    {
      id: 'mock-sched-1',
      symbol: '005930',
      stock_name: '삼성전자',
      order_type: 'buy',
      quantity: 2,
      price: 74000,
      status: 'pending',
      scheduled_time: new Date(Date.now() + 3600000).toISOString(),
    },
  ],
  purchaseAnalyses: [
    {
      id: 'mock-analysis-1',
      symbol: '000660',
      stock_name: 'SK하이닉스',
      current_status: '구매예정',
      recommendation: { action: 'buy', confidence: 82 },
      trade_config: { order_type: 'buy', quantity: 1, price: 205000, scheduled_time: new Date(Date.now() + 7200000).toISOString() },
      scheduled_order_id: 'mock-sched-1',
      execution_status: 'pending',
    },
  ],
  tradingSettings: {
    blocked_symbols: [],
    blocked_buy_symbols: [],
    blocked_sell_symbols: [],
  },
};

const mockDelay = () => new Promise((r) => setTimeout(r, 150));

/** 로그인(토큰) 있으면 목업 금지 → 실 시세·실 주문 */
function shouldUseMock() {
  if (getToken()) return false;
  return ENV_ALLOW_MOCK;
}

function isAuthError(err) {
  return err?.status === 401 || err?.message === 'Authentication required';
}

async function apiFetch(path, options = {}) {
  if (!getToken()) {
    const err = new Error('Authentication required');
    err.status = 401;
    throw err;
  }
  try {
    const response = await authenticatedFetch(`/api${path}`, {
      timeout: 60000,
      ...options,
    });
    if (!response.ok) {
      const err = new Error(`API ${response.status}: ${path}`);
      err.status = response.status;
      throw err;
    }
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('text/html')) {
      throw new Error(`API HTML response: ${path}`);
    }
    return response.json();
  } catch (err) {
    if (isAuthError(err)) {
      const authErr = new Error('Authentication required');
      authErr.status = 401;
      throw authErr;
    }
    throw err;
  }
}

/** 읽기 전용 API — 미로그인 시 throw 대신 null (uncaught promise 방지) */
async function apiFetchOptional(path, options = {}) {
  if (!getToken()) return null;
  try {
    return await apiFetch(path, options);
  } catch (err) {
    if (isAuthError(err)) return null;
    throw err;
  }
}

async function apiPost(path, body = {}) {
  return apiFetch(path, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

function normalizeBalance(data = {}) {
  return {
    total_assets: data.totalValue ?? data.total_assets ?? data.total_value ?? 0,
    cash: data.cash ?? 0,
    available_cash: data.availableCash ?? data.available_cash ?? data.cash ?? 0,
    stock_value: data.stock_value ?? data.stockValue ?? 0,
    profit_loss: data.profit_loss ?? data.profitLoss ?? 0,
    profit_loss_rate: data.profit_loss_rate ?? data.profitLossRate ?? 0,
    holdings_count: data.holdings ?? data.holdings_count ?? 0,
    message: data.message,
  };
}

function normalizeHolding(h = {}) {
  const currentPrice = h.currentPrice ?? h.current_price ?? h.price ?? 0;
  const averagePrice = h.averagePrice ?? h.avg_price ?? h.avgPrice ?? 0;
  const quantity = h.quantity ?? 0;
  const profitLoss = h.profit_loss ?? h.profitLoss ?? h.profit ?? 0;
  const profitLossRate = h.profit_loss_rate ?? h.profitLossRate ?? h.profitRate ?? 0;
  return {
    symbol: h.symbol,
    name: h.name || h.stock_name || h.stockName || h.symbol,
    quantity,
    averagePrice,
    currentPrice,
    profit_loss: profitLoss,
    profit_loss_rate: profitLossRate,
    total_value: h.total_value ?? h.totalValue ?? quantity * currentPrice,
  };
}

function mapRecommendations(items = []) {
  return items.map((item) => ({
    symbol: item.symbol,
    name: item.stock_name || item.stockName || item.name || item.symbol,
    action: item.action || 'buy',
    confidence: item.confidence ?? 0,
    reason: item.reasoning || item.reason || '',
  }));
}

function normalizeOrder(o = {}) {
  return {
    id: o._id || o.id || o.order_id || o.orderId,
    symbol: o.symbol || o.code || o.ticker,
    stock_name: o.stock_name || o.stockName || o.name || o.symbol,
    order_type: (o.order_type || o.decision || 'buy').toString().toLowerCase(),
    quantity: o.quantity ?? o.qty ?? 0,
    price: o.price ?? o.order_price ?? o.avg_price ?? 0,
    status: (o.status || 'pending').toString().toLowerCase(),
    created_at: o.created_at || o.createdAt || o.timestamp || o.ordered_at,
  };
}

/** UI용: 로그인 시 false(실실행), 미로그인+목업빌드만 true */
export function isMockMode() {
  return shouldUseMock();
}

export function hasTradingToken() {
  return !!getToken();
}

export async function fetchBalance() {
  if (shouldUseMock()) {
    await mockDelay();
    return { balance: MOCK.balance };
  }
  const data = await apiFetchOptional('/portfolio/balance');
  return { balance: data ? normalizeBalance(data) : null };
}

export async function fetchPortfolio() {
  if (shouldUseMock()) {
    await mockDelay();
    return { portfolio: MOCK.portfolio };
  }
  const data = await apiFetchOptional('/portfolio');
  if (!data) return { portfolio: [] };
  const raw = data.portfolio?.holdings || data.portfolio || [];
  const list = Array.isArray(raw) ? raw : [];
  return { portfolio: list.map(normalizeHolding) };
}

export async function fetchRecentTrades() {
  if (shouldUseMock()) {
    await mockDelay();
    return { trades: MOCK.trades };
  }
  const data = await apiFetchOptional('/portfolio/history?days=7&limit=10');
  return { trades: data?.trades || [] };
}

/** 주문 내역(대기·체결·취소 포함) */
export async function fetchOrderHistory({ days = 7, limit = 30 } = {}) {
  if (shouldUseMock()) {
    await mockDelay();
    const completed = MOCK.trades.map((t) => normalizeOrder({ ...t, status: t.status || 'completed' }));
    return { orders: [...mockPendingOrders.map(normalizeOrder), ...completed] };
  }
  const data = await apiFetchOptional(`/portfolio/history?days=${days}&limit=${limit}`);
  if (!data) return { orders: [] };
  const raw = data.orders || data.trades || data.history || [];
  return { orders: (Array.isArray(raw) ? raw : []).map(normalizeOrder) };
}

export async function fetchAIRecommendations() {
  if (shouldUseMock()) {
    await mockDelay();
    return { recommendations: MOCK.recommendations };
  }
  try {
    const data = await apiPost('/ai-trade/discover', { max_results: 10 });
    return { recommendations: mapRecommendations(data.recommendations || []) };
  } catch (err) {
    if (err.status !== 404) console.warn('[StockAI] AI 추천 조회 실패:', err.message);
    return { recommendations: [] };
  }
}

export async function fetchAIConfig() {
  if (shouldUseMock()) {
    await mockDelay();
    return { config: MOCK.config };
  }
  const data = await apiFetchOptional('/ai-trade/config');
  return { config: data ? (data.config || data) : null };
}

const MOCK_SEARCH_CATALOG = [
  { symbol: '005930', name: '삼성전자', market: 'KOSPI', price: 74500 },
  { symbol: '000660', name: 'SK하이닉스', market: 'KOSPI', price: 205000 },
  { symbol: '035420', name: 'NAVER', market: 'KOSPI', price: 220000 },
  { symbol: '051910', name: 'LG화학', market: 'KOSPI', price: 310000 },
  { symbol: '006400', name: '삼성SDI', market: 'KOSPI', price: 380000 },
];

function mapSearchResult(item = {}) {
  return {
    symbol: item.symbol,
    name: item.name || item.stock_name || item.stockName || item.symbol,
    market: item.market || '',
    price: item.price ?? item.currentPrice ?? item.current_price ?? 0,
  };
}

export async function searchStocks(keyword) {
  const q = (keyword || '').trim();
  if (!q) return { results: [] };

  // 미로그인 목업만 카탈로그 사용. 로그인 시 실 시세 API.
  if (shouldUseMock()) {
    await mockDelay();
    const lower = q.toLowerCase();
    const results = MOCK_SEARCH_CATALOG.filter(
      (s) => s.symbol.includes(q) || s.name.toLowerCase().includes(lower)
    );
    return { results };
  }

  if (!getToken()) {
    return { results: [] };
  }

  if (/^\d{6}$/.test(q)) {
    try {
      const response = await authenticatedFetch(`/api/trading/stock/${q}`, { timeout: 30000 });
      if (response.ok) {
        const data = await response.json();
        return { results: [mapSearchResult({ ...data, symbol: data.symbol || q })] };
      }
    } catch (err) {
      if (!isAuthError(err)) console.warn('[StockAI] 종목코드 조회 실패:', err.message);
    }
  }

  try {
    const response = await authenticatedFetch(
      `/api/trading/search?keyword=${encodeURIComponent(q)}`,
      { timeout: 30000 }
    );
    if (response.ok) {
      const data = await response.json();
      const list = data.results || data.stocks || [];
      return { results: list.map(mapSearchResult) };
    }
  } catch (err) {
    if (!isAuthError(err)) console.warn('[StockAI] 종목 검색 실패:', err.message);
  }
  return { results: [] };
}

export async function fetchStockQuote(symbol) {
  if (!symbol) return null;

  if (shouldUseMock()) {
    await mockDelay();
    const hit = MOCK_SEARCH_CATALOG.find((s) => s.symbol === symbol)
      || MOCK.portfolio.find((s) => s.symbol === symbol);
    return hit ? mapSearchResult(hit) : { symbol, name: symbol, price: 0 };
  }

  if (!getToken()) return null;

  try {
    const response = await authenticatedFetch(`/api/trading/stock/${symbol}`, { timeout: 30000 });
    if (response.ok) {
      const data = await response.json();
      return mapSearchResult({ ...data, symbol: data.symbol || symbol });
    }
  } catch (err) {
    if (!isAuthError(err)) console.warn('[StockAI] 시세 조회 실패:', err.message);
  }
  return null;
}

export async function placeOrder({ symbol, order_type, quantity, price, order_method = 'limit' }) {
  if (shouldUseMock()) {
    await mockDelay();
    const id = `mock-pending-${mockOrderSeq++}`;
    const order = normalizeOrder({
      id,
      symbol,
      stock_name: MOCK_SEARCH_CATALOG.find((s) => s.symbol === symbol)?.name || symbol,
      order_type,
      quantity: Number(quantity),
      price: price != null && price !== '' ? Number(price) : 0,
      status: 'pending',
      created_at: new Date().toISOString(),
    });
    mockPendingOrders.unshift(order);
    return {
      success: true,
      status: 'pending',
      order_id: id,
      order,
      message: `[Mock] ${order_type === 'buy' ? '매수' : '매도'} ${symbol} ${quantity}주 대기 접수`,
    };
  }
  const body = {
    symbol,
    order_type,
    quantity: Number(quantity),
    order_method,
  };
  if (price != null && price !== '') {
    body.price = Number(price);
  }
  const data = await apiPost('/trading/order', body);
  return {
    success: true,
    status: data.status || 'pending',
    message: data.message || '주문이 접수되었습니다.',
    ...data,
  };
}

export async function cancelOrder(orderId) {
  if (!orderId) throw new Error('주문 ID가 없습니다.');

  if (shouldUseMock()) {
    await mockDelay();
    const idx = mockPendingOrders.findIndex((o) => String(o.id) === String(orderId));
    if (idx === -1) throw new Error('대기 주문을 찾을 수 없습니다.');
    mockPendingOrders.splice(idx, 1);
    return { success: true, message: '[Mock] 주문이 취소되었습니다.' };
  }

  const response = await authenticatedFetch(`/api/trading/order/${orderId}`, {
    method: 'DELETE',
    timeout: 30000,
  });
  if (!response.ok) {
    let message = `주문 취소 실패 (${response.status})`;
    try {
      const errBody = await response.json();
      message = errBody.error || errBody.message || message;
    } catch (_) { /* ignore */ }
    const err = new Error(message);
    err.status = response.status;
    throw err;
  }
  const data = await response.json().catch(() => ({}));
  return { success: true, message: data.message || '주문이 취소되었습니다.', ...data };
}

function normalizeScheduledOrder(o = {}) {
  return {
    id: o._id || o.id || o.order_id,
    symbol: o.symbol,
    stock_name: o.stock_name || o.stockName || o.name || o.symbol,
    order_type: (o.order_type || 'buy').toString().toLowerCase(),
    quantity: o.quantity ?? 0,
    price: o.price ?? 0,
    status: (o.status || 'pending').toString().toLowerCase(),
    scheduled_time: o.scheduled_time || o.next_execution_time || o.scheduledTime,
    analysis_id: o.metadata?.analysis_id || o.analysis_id,
  };
}

export async function fetchAIConnectionStatus() {
  if (shouldUseMock()) {
    await mockDelay();
    return { ...MOCK.connection };
  }
  const data = await apiFetchOptional('/ai-trade/connection-status');
  return data || {
    google_ai: { connected: false, message: '조회 불가' },
    local_ai: { connected: false, message: '조회 불가' },
  };
}

export async function fetchAITradeStatus() {
  if (shouldUseMock()) {
    await mockDelay();
    return { ...MOCK.aiStatus };
  }
  const data = await apiFetchOptional('/ai-trade/status');
  return data || null;
}

export async function fetchScheduledOrders() {
  if (shouldUseMock()) {
    await mockDelay();
    return { orders: MOCK.scheduledOrders.map(normalizeScheduledOrder) };
  }
  const data = await apiFetchOptional('/portfolio/scheduled-orders');
  if (!data) return { orders: [] };
  const raw = data.orders || data.scheduledOrders || [];
  return { orders: (Array.isArray(raw) ? raw : []).map(normalizeScheduledOrder) };
}

export async function cancelScheduledOrder(orderId) {
  if (!orderId) throw new Error('예약 주문 ID가 없습니다.');
  if (shouldUseMock()) {
    await mockDelay();
    const idx = MOCK.scheduledOrders.findIndex((o) => String(o.id) === String(orderId));
    if (idx >= 0) MOCK.scheduledOrders[idx].status = 'cancelled';
    return { success: true, message: '[Mock] 예약 주문이 취소되었습니다.' };
  }
  await apiFetch(`/portfolio/scheduled-orders/${orderId}`, { method: 'DELETE' });
  return { success: true, message: '예약 주문이 취소되었습니다.' };
}

export async function fetchPurchaseAnalyses() {
  if (shouldUseMock()) {
    await mockDelay();
    return { analyses: MOCK.purchaseAnalyses };
  }
  const data = await apiFetchOptional('/ai-trade/analyses?status=' + encodeURIComponent('구매예정'));
  if (!data) return { analyses: [] };
  const list = data.analyses || [];
  return {
    analyses: list.map((a) => ({
      id: a._id || a.id,
      symbol: a.symbol,
      stock_name: a.stock_name || a.stockName || a.name || a.symbol,
      current_status: a.current_status,
      recommendation: a.recommendation || {},
      trade_config: a.trade_config || {},
      scheduled_order_id: a.scheduled_order_id,
      execution_status: a.execution_status || 'pending',
      next_analysis_time: a.next_analysis_time,
    })),
  };
}

/** 대기: 예약 시각 제거·실행 보류 */
export async function holdAnalysisSchedule(analysisId, tradeConfig = {}) {
  if (!analysisId) throw new Error('분석 ID가 없습니다.');
  if (shouldUseMock()) {
    await mockDelay();
    const hit = MOCK.purchaseAnalyses.find((a) => String(a.id) === String(analysisId));
    if (hit) {
      hit.execution_status = 'pending';
      hit.trade_config = { ...hit.trade_config, scheduled_time: null };
      hit.current_status = '관찰중';
    }
    return { success: true, message: '[Mock] 대기(보류)로 전환했습니다.' };
  }
  const data = await apiFetch(`/ai-trade/analyses/${analysisId}/plan`, {
    method: 'PUT',
    body: JSON.stringify({
      trade_config: { ...tradeConfig, scheduled_time: null },
      plan: { strategy: 'hold', timeline: '사용자 대기' },
    }),
  });
  return { success: true, message: '대기(보류)로 전환했습니다.', ...data };
}

export async function fetchTradingSettings() {
  if (shouldUseMock()) {
    await mockDelay();
    return {
      nickname: 'mock',
      name: 'mock',
      trading: { ...MOCK.tradingSettings },
    };
  }
  const data = await apiFetchOptional('/user/me');
  if (!data) return { trading: { blocked_symbols: [], blocked_buy_symbols: [], blocked_sell_symbols: [] } };
  const user = data.user || data;
  return {
    nickname: user.nickname || '',
    name: user.name || '',
    trading: {
      ...(user.trading || {}),
      blocked_symbols: user.trading?.blocked_symbols || [],
      blocked_buy_symbols: user.trading?.blocked_buy_symbols || [],
      blocked_sell_symbols: user.trading?.blocked_sell_symbols || [],
    },
  };
}

/**
 * @param {'both'|'buy'|'sell'} mode
 * @param {boolean} enable true=추가, false=제거
 */
export async function setSymbolTradeBan(symbol, mode = 'sell', enable = true) {
  const sym = String(symbol || '').trim();
  if (!sym) throw new Error('종목코드가 없습니다.');

  if (shouldUseMock()) {
    await mockDelay();
    const t = MOCK.tradingSettings;
    const add = (arr) => (arr.includes(sym) ? arr : [...arr, sym]);
    const remove = (arr) => arr.filter((s) => s !== sym);
    if (mode === 'both') {
      t.blocked_symbols = enable ? add(t.blocked_symbols) : remove(t.blocked_symbols);
      t.blocked_buy_symbols = remove(t.blocked_buy_symbols);
      t.blocked_sell_symbols = remove(t.blocked_sell_symbols);
    } else if (mode === 'buy') {
      t.blocked_buy_symbols = enable ? add(t.blocked_buy_symbols) : remove(t.blocked_buy_symbols);
      if (enable) t.blocked_symbols = remove(t.blocked_symbols);
    } else {
      t.blocked_sell_symbols = enable ? add(t.blocked_sell_symbols) : remove(t.blocked_sell_symbols);
      if (enable) t.blocked_symbols = remove(t.blocked_symbols);
    }
    return { success: true, trading: { ...t }, message: enable ? '거래 금지 적용' : '거래 금지 해제' };
  }

  const current = await fetchTradingSettings();
  const trading = { ...(current.trading || {}) };
  let blocked = [...(trading.blocked_symbols || [])];
  let blockedBuy = [...(trading.blocked_buy_symbols || [])];
  let blockedSell = [...(trading.blocked_sell_symbols || [])];
  const add = (arr) => (arr.includes(sym) ? arr : [...arr, sym]);
  const remove = (arr) => arr.filter((s) => s !== sym);

  if (mode === 'both') {
    blocked = enable ? add(blocked) : remove(blocked);
    if (enable) {
      blockedBuy = remove(blockedBuy);
      blockedSell = remove(blockedSell);
    }
  } else if (mode === 'buy') {
    blockedBuy = enable ? add(blockedBuy) : remove(blockedBuy);
    if (enable) blocked = remove(blocked);
  } else {
    blockedSell = enable ? add(blockedSell) : remove(blockedSell);
    if (enable) blocked = remove(blocked);
  }

  const body = {
    nickname: current.nickname || '',
    name: current.name || '',
    trading: {
      ...trading,
      blocked_symbols: blocked,
      blocked_buy_symbols: blockedBuy,
      blocked_sell_symbols: blockedSell,
    },
  };

  const data = await apiFetch('/user/settings', {
    method: 'PUT',
    body: JSON.stringify(body),
  });

  return {
    success: true,
    trading: body.trading,
    message: enable ? '거래 금지 적용' : '거래 금지 해제',
    ...data,
  };
}
